/**
 * Authentication Utilities
 *
 * Thin localStorage layer over the auth data mirrored by authSync.
 * The session itself lives in the SSO provider (see src/auth/sso.ts);
 * these helpers only read the generic keys.
 */

import { AUTH_CONFIG } from '@/config/constants';
import type { CurrentUser } from '@/types';

/**
 * Get the mirrored access token (may be stale — prefer getSsoToken()
 * from src/auth/sso.ts for API calls).
 */
export const getToken = (): string | null => {
  return localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
};

/**
 * Clear all mirrored authentication data
 */
export const clearAuthData = (): void => {
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.ROLE_KEY);
  localStorage.removeItem(AUTH_CONFIG.ROLES_KEY);
  localStorage.removeItem(AUTH_CONFIG.USER_KEY);
  localStorage.removeItem(AUTH_CONFIG.AUTH_METHOD_KEY);
};

// ============================================
// USER MANAGEMENT
// ============================================

/**
 * Get current user from localStorage
 */
export const getCurrentUser = (): CurrentUser | null => {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson) as CurrentUser;
  } catch {
    return null;
  }
};

/**
 * Get current user's primary role
 */
export const getCurrentRole = (): string | null => {
  return localStorage.getItem(AUTH_CONFIG.ROLE_KEY) || null;
};

/**
 * Get every app role the user has
 */
export const getCurrentRoles = (): string[] => {
  const rolesJson = localStorage.getItem(AUTH_CONFIG.ROLES_KEY);
  if (!rolesJson) return [];

  try {
    const parsed: unknown = JSON.parse(rolesJson);
    return Array.isArray(parsed) ? parsed.filter((r): r is string => typeof r === 'string') : [];
  } catch {
    return [];
  }
};

// ============================================
// PERMISSION CHECKS
// ============================================

/**
 * Check if user has a specific role
 */
export const hasRole = (requiredRole: string): boolean => {
  return getCurrentRoles().includes(requiredRole);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (requiredRoles: string[]): boolean => {
  const roles = getCurrentRoles();
  return requiredRoles.some((role) => roles.includes(role));
};
